import { useQuery } from "@tanstack/react-query";
import { salesDb } from "@/integrations/supabase/sales-client";
import type { StudentKind } from "./useEventBalances";

export type HistoryRow = {
  id: string;
  status: string;
  assigned_at: string;
  unassigned_at: string | null;
  event_id: string;
  event_name: string | null;
  event_start_at: string | null;
  event_location: string | null;
};

export const useStudentEventHistory = (
  studentCode: string | null,
  studentKind: StudentKind | null
) => {
  return useQuery({
    enabled: !!studentCode && !!studentKind,
    queryKey: ["student-event-history", studentKind, studentCode],
    queryFn: async () => {
      const col =
        studentKind === "esc"
          ? "esc_student_code"
          : studentKind === "kn"
          ? "knoware_student_code"
          : "fishsix_student_code";
      const { data, error } = await salesDb()
        .from("event_assignments")
        .select("id,status,assigned_at,unassigned_at,event_id")
        .eq(col, studentCode)
        .order("assigned_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as any[];
      const ids = Array.from(new Set(rows.map((r) => r.event_id)));
      if (!ids.length) return [] as HistoryRow[];
      const { data: events } = await salesDb()
        .from("events")
        .select("id,name,start_at,location")
        .in("id", ids);
      const m = new Map<string, any>();
      (events ?? []).forEach((e: any) => m.set(e.id, e));
      return rows.map((r) => ({
        id: r.id,
        status: r.status,
        assigned_at: r.assigned_at,
        unassigned_at: r.unassigned_at,
        event_id: r.event_id,
        event_name: m.get(r.event_id)?.name ?? null,
        event_start_at: m.get(r.event_id)?.start_at ?? null,
        event_location: m.get(r.event_id)?.location ?? null,
      })) as HistoryRow[];
    },
  });
};
